
import { useState } from 'react';
import { useAuth } from '@/contexts/auth';
import { useToast } from '@/hooks/use-toast';
import { EnhancedPrayerService } from '@/services/EnhancedPrayerService';
import { AnonymousUserService } from '@/services/AnonymousUserService';

export function useEnhancedPrayerGeneration() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedPrayer, setGeneratedPrayer] = useState<string | null>(null);
  const [prayerRequest, setPrayerRequest] = useState<any>(null);

  const generatePrayer = async (requestData: {
    request_text: string;
    subject?: string;
    selected_tags?: string[];
    include_bible_verse?: boolean;
  }) => {
    setIsGenerating(true);
    
    try {
      // Create prayer request in database
      const request = await EnhancedPrayerService.createAIPrayerRequest(
        requestData,
        user?.id
      );
      
      setPrayerRequest(request);

      // Call AI prayer generation service
      const response = await fetch('/api/generate-prayer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error('Failed to generate prayer');
      }

      const { prayer } = await response.json();
      setGeneratedPrayer(prayer);

      // Save the generated prayer
      if (prayer) {
        const { userPrayer, abielPrayer } = await EnhancedPrayerService.saveGeneratedPrayer(
          prayer,
          request,
          user?.id
        );

        // Show appropriate success message
        if (user) {
          toast({
            title: "Prayer Generated & Saved",
            description: "Your prayer has been saved to your private collection, and Abiel has added it to the 'Prayed For' collection.",
          });
        } else {
          toast({
            title: "Prayer Generated",
            description: "Your prayer has been generated. Sign up to save it permanently!",
          });
        }

        return { prayer, userPrayer, abielPrayer, request };
      }
    } catch (error: any) {
      console.error('Error generating prayer:', error);
      toast({
        title: "Generation Failed",
        description: error.message || "Failed to generate prayer. Please try again.",
        variant: "destructive"
      });
      throw error;
    } finally {
      setIsGenerating(false);
    }
  };

  const recordChoiceAction = async (
    prayerId: string,
    actionType: 'i_need_prayer' | 'i_want_to_pray',
    choiceMethod: 'ai_assistant' | 'manual_prayer'
  ) => {
    try {
      return await EnhancedPrayerService.recordPrayerChoiceAction(
        prayerId,
        actionType,
        choiceMethod,
        user?.id
      );
    } catch (error: any) {
      console.error('Error recording choice action:', error);
      toast({
        title: "Tracking Error",
        description: "Failed to record your choice, but prayer functionality continues.",
        variant: "destructive"
      });
    }
  };

  const getAnonymousDataStatus = () => {
    return {
      hasAnonymousData: AnonymousUserService.hasAnonymousData(),
      anonymousRequests: AnonymousUserService.getPrayerRequests(),
      sessionId: AnonymousUserService.getSessionId()
    };
  };

  const clearAnonymousData = () => {
    AnonymousUserService.clearAnonymousData();
  };

  return {
    isGenerating,
    generatedPrayer,
    prayerRequest,
    generatePrayer,
    recordChoiceAction,
    getAnonymousDataStatus,
    clearAnonymousData,
    isAuthenticated: !!user,
    user
  };
}
